import { defineMessages } from 'react-intl-next';

export const standardMessages = defineMessages({
	standardBenefitsModalTitle: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.title',
		defaultMessage: 'Explore your trial',
		description: 'Title for the standard benefits modal',
	},
	standardBenefitsModalBillingContent: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.billing-content',
		defaultMessage: 'Add your payment details by 15 September to continue using Standard. {plansLink}',
		description:
			'Billing content for the standard benefits modal. {plansLink} is the “See all plans” link to pricing.',
	},
	standardBenefitsModalBillingPlansLink: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.billing-plans-link',
		defaultMessage: 'See all plans.',
		description: 'Link label for the billing line “See all plans” in the standard benefits modal',
	},
	standardBenefitsModalBillingContentPaymentEntered: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.billing-content-payment-entered',
		defaultMessage:
			'You won’t be charged until your next billing date on {billingDate}. {plansLink} or {cancelLink}',
		description:
			'Billing line when payment details are on file. {billingDate} is the next billing date; {plansLink} and {cancelLink} are inline links.',
	},
	standardBenefitsModalBillingSeePlansLink: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.billing-see-plans-link',
		defaultMessage: 'See plans',
		description: 'Link label “See plans” in the payment-entered billing line',
	},
	standardBenefitsModalBillingCancelAnytimeLink: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.billing-cancel-anytime-link',
		defaultMessage: 'cancel anytime.',
		description: 'Link label “cancel anytime.” in the payment-entered billing line',
	},
	standardBenefitsModalAddPaymentDetailsButton: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.button.add-payment-details',
		defaultMessage: 'Add payment details',
		description: 'Text for the add payment details button in the standard benefits modal',
	},
	standardBenefitsModalExploreRovoServiceAgentButton: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.button.explore-rovo-service-agent',
		defaultMessage: 'Explore Rovo Service agent',
		description: 'Text for the explore Rovo Service agent button in the standard benefits modal',
	},
	standardBenefitsModalExploreKnowledgeBaseButton: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.button.explore-knowledge-base',
		defaultMessage: 'Explore Knowledge base',
		description: 'Text for the explore Knowledge base primary button when that feature is selected',
	},
	standardBenefitsModalExploreAutomationsButton: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.button.explore-automations',
		defaultMessage: 'Explore Automations',
		description: 'Text for the explore Automations primary button when that feature is selected',
	},
	standardBenefitsModalInviteMoreAgentsButton: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.button.invite-more-agents',
		defaultMessage: 'Invite more agents',
		description: 'Text for the invite more agents primary button when unlimited agents is selected',
	},
	// CTA — CSM
	standardBenefitsModalExploreCustomerServiceAgentButton: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.button.explore-customer-service-agent',
		defaultMessage: 'Explore Customer service agent',
		description: 'Primary CTA when Customer service agent feature is selected (CSM)',
	},
	standardBenefitsModalExploreRovoSupportCompanionButton: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.button.explore-rovo-support-companion',
		defaultMessage: 'Explore Rovo Support Companion',
		description: 'Primary CTA when Rovo Support Companion feature is selected (CSM)',
	},
	standardBenefitsModalExploreAiEnabledFormsButton: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.button.explore-ai-enabled-forms',
		defaultMessage: 'Explore AI enabled Forms',
		description: 'Primary CTA when AI enabled Forms feature is selected (CSM)',
	},
	// CTA — Ops
	standardBenefitsModalExploreRovoOpsAgentButton: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.button.explore-rovo-ops-agent',
		defaultMessage: 'Explore Rovo Ops agent',
		description: 'Primary CTA when Rovo Ops agent feature is selected (Ops)',
	},
	standardBenefitsModalExploreAssetsButton: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.button.explore-assets',
		defaultMessage: 'Explore Assets',
		description: 'Primary CTA when Assets feature is selected (Ops)',
	},
	// Feature: Service Teams
	serviceTeamsFeatureRovoAgentDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.service-teams.rovo-service-agents.description',
		defaultMessage: 'Use AI-powered <strong>Rovo Service agent</strong> to resolve requests faster',
		description: 'Description for the Rovo Service Agents feature',
	},
	serviceTeamsFeatureKnowledgeBaseDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.service-teams.knowledge-base.description',
		defaultMessage: 'Power your Rovo agent with <strong>AI written Knowledge Base articles</strong>',
		description: 'Description for the Knowledge Base feature',
	},
	serviceTeamsFeatureAutomationRulesDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.service-teams.automation-rules.description',
		defaultMessage: 'Keep your requests moving with <strong>10x Automation rule runs</strong>',
		description: 'Description for the Automation Rules feature',
	},
	serviceTeamsFeatureUnlimitedAgentsDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.service-teams.unlimited-agents.description',
		defaultMessage: '<strong>Unlimited agents</strong> to support your customer needs',
		description: 'Description for the Unlimited Agents feature',
	},
	// Feature: CSM
	csmFeatureCustomerServiceAgentDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.csm.customer-service-agent.description',
		defaultMessage:
			'Use AI powered <strong>Customer service agent</strong> to deflect common requests',
		description: 'Description for the Customer service agent feature (CSM)',
	},
	csmFeatureRovoSupportCompanionDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.csm.rovo-support-companion.description',
		defaultMessage:
			'Use <strong>Rovo Support Companion</strong> to help your human agents resolve requests faster',
		description: 'Description for the Rovo Support Companion feature (CSM)',
	},
	csmFeatureAiEnabledFormsDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.csm.ai-enabled-forms.description',
		defaultMessage:
			'Create <strong>AI enabled Forms</strong> to automatically convert customer chats into requests in your queue',
		description: 'Description for the AI enabled Forms feature (CSM)',
	},
	csmFeatureUnlimitedAgentsDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.csm.unlimited-agents.description',
		defaultMessage: '<strong>Unlimited agents</strong> to support your customer needs',
		description: 'Description for the Unlimited agents feature (CSM)',
	},
	// Feature: Ops
	opsFeatureRovoOpsAgentDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.ops.rovo-ops-agent.description',
		defaultMessage:
			'Use AI-powered <strong>Rovo Ops agent</strong> to get AI summaries and next steps for incidents',
		description: 'Description for the Rovo Ops agent feature (Ops)',
	},
	opsFeatureAssetsDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.ops.assets.description',
		defaultMessage:
			'See impacted business critical objects for rapid root cause analysis with <strong>Assets</strong>',
		description: 'Description for the Assets feature (Ops)',
	},
	opsFeatureAutomationRulesDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.ops.automation-rules.description',
		defaultMessage: 'Move forward faster with <strong>10x Automation rule runs</strong>',
		description: 'Description for the Automation rule runs feature (Ops)',
	},
	opsFeatureUnlimitedAgentsDescription: {
		id: '@atlassian/edition-benefits-modal.standard-benefits-modal.feature.ops.unlimited-agents.description',
		defaultMessage: '<strong>Unlimited agents</strong> to support your customer needs',
		description: 'Description for the Unlimited agents feature (Ops)',
	},
});
